import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import { IconButton, Tooltip } from '@mui/material';
import { useExportGuestJourneyDetailsCustomersMutation } from 'api/reports-service/guest-journey-reports';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectIsHideDownloadCustomerListOption } from 'redux/client-config/client-config.selectors';
import { selectEmail } from 'redux/user/user.selectors';
import { getClientAlias } from 'util/client-alias';
import { downloadCSV } from 'util/download-csv.utils';

export type DetailsSegmentExportButtonProps = {
  /**
   * Machine segment key (`SPEND_TIER_x_VISIT_TIER_y`) for this row's
   * spend×frequency segment. Undefined until the BE emits it (§D6) — the button
   * is disabled while it is absent.
   */
  segment?: string;
  /** Active Details filters, forwarded so the export matches the on-screen view. */
  params?: Record<string, unknown>;
  'data-testid'?: string;
};

/**
 * Per-row "export the customers in this segment" action for the Details tables
 * (sc-357751 QA, activity 368127). Mirrors the CRM `CustomersExportButton`: the
 * export is emailed by the BE (or returned inline as a CSV when the response
 * status is `DOWNLOADED`), and the whole control is hidden for clients that
 * disable customer-list downloads. It sends the machine `segment` key rather
 * than the display stage name, which is client-renamable.
 */
const DetailsSegmentExportButton = ({
  segment,
  params,
  'data-testid': dataTestId,
}: DetailsSegmentExportButtonProps) => {
  const { t } = useTranslation();
  const email = useSelector(selectEmail);
  const { enqueueSnackbar } = useSnackbar();
  const isHideDownloadCustomerListOption = useSelector(
    selectIsHideDownloadCustomerListOption,
  );

  const [exportCustomers, { isLoading, isSuccess, data }] =
    useExportGuestJourneyDetailsCustomersMutation();

  useEffect(() => {
    if (!isSuccess || !data) return;
    if (data.status === 'DOWNLOADED' && data.data) {
      downloadCSV(
        data.data.REPORT,
        `${getClientAlias()}_guest_journey_${segment}_${DateTime.local().toFormat(
          'yyyy_MM_dd__HH_mm_ss',
        )}`.toLowerCase(),
      );
    } else {
      enqueueSnackbar(data.responseMessage, { severity: 'success' });
    }
  }, [data, enqueueSnackbar, isSuccess, segment]);

  if (isHideDownloadCustomerListOption) return null;

  return (
    <Tooltip title={t('page.guestJourney.details.table.exportTooltip')}>
      <span>
        <IconButton
          size="small"
          data-testid={dataTestId}
          disabled={isLoading || !segment}
          onClick={() =>
            exportCustomers({ segment: segment as string, email, params })
          }
        >
          <DownloadOutlined fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default DetailsSegmentExportButton;
